import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { connect as twilioConnect, createLocalTracks } from "twilio-video";
import {
  X, VideoCamera, VideoCameraSlash, Microphone, MicrophoneSlash,
  Phone, Spinner, Record, ArrowsOut,
} from "@phosphor-icons/react";
import { mintVideoToken, logVideoEnd, transcribeAudio } from "@/lib/api";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";

/**
 * VideoCallModal
 * ----------------
 * Renders a full-screen Twilio Video room (1:1 or group). The admin/staff
 * who opens it joins as `identity = userEmail`, the room name is auto-
 * generated unless the caller passes one in. Includes mute/unmute,
 * camera on/off, optional MediaRecorder capture for later Whisper
 * transcription, and "End call" (logged to backend).
 */
export default function VideoCallModal({
  open,
  onClose,
  customerId,
  customerName = "",
  personName = "",
  userEmail,
  initialRoom,
}) {
  const [phase, setPhase] = useState("idle"); // idle | connecting | live | ended | error
  const [error, setError] = useState(null);
  const [room, setRoom] = useState(null);
  const [roomName, setRoomName] = useState(initialRoom || "");
  const [identity, setIdentity] = useState("");
  const [participants, setParticipants] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const localVideoRef = useRef(null);
  const remoteContainerRef = useRef(null);
  const localTracksRef = useRef([]);
  const recorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const recorderStreamRef = useRef(null);
  const startedAtRef = useRef(null);

  // ─── Lifecycle: connect on open ────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setPhase("connecting"); setError(null);
      try {
        const desiredRoom = initialRoom || `customer-${customerId}-${Date.now()}`;
        const { token, room: serverRoom, identity: id } =
          await mintVideoToken({ room: desiredRoom, identity: userEmail, customer_id: customerId });
        if (cancelled) return;
        setRoomName(serverRoom);
        setIdentity(id);
        setShareableLink(`${window.location.origin}/video?room=${encodeURIComponent(serverRoom)}`);

        const tracks = await createLocalTracks({ audio: true, video: { width: 640 } });
        localTracksRef.current = tracks;
        const videoTrack = tracks.find(t => t.kind === "video");
        if (videoTrack && localVideoRef.current) {
          localVideoRef.current.appendChild(videoTrack.attach());
        }

        const r = await twilioConnect(token, { name: serverRoom, tracks });
        if (cancelled) { try { r.disconnect(); } catch { /* noop */ } return; }
        setRoom(r);
        startedAtRef.current = Date.now();
        setPhase("live");

        const refreshParticipants = () => {
          setParticipants(Array.from(r.participants.values()).map(p => ({ sid: p.sid, identity: p.identity })));
        };
        refreshParticipants();

        const wireRemote = (participant) => {
          refreshParticipants();
          const onSubscribed = (track) => {
            if (remoteContainerRef.current) {
              const el = track.attach();
              el.dataset.participantSid = participant.sid;
              el.style.width = "100%";
              el.style.borderRadius = "8px";
              remoteContainerRef.current.appendChild(el);
            }
          };
          participant.tracks.forEach(pub => { if (pub.track) onSubscribed(pub.track); });
          participant.on("trackSubscribed", onSubscribed);
        };
        r.participants.forEach(wireRemote);
        r.on("participantConnected", wireRemote);
        r.on("participantDisconnected", () => refreshParticipants());
        r.on("disconnected", () => setPhase("ended"));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || "Failed to start video call");
          setPhase("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Elapsed timer
  useEffect(() => {
    if (phase !== "live") return;
    const id = setInterval(() => {
      if (startedAtRef.current) setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const teardown = useCallback(() => {
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch { /* noop */ }
    try { recorderStreamRef.current?.getTracks?.().forEach(t => t.stop()); } catch { /* noop */ }
    try {
      localTracksRef.current.forEach(t => { try { t.stop(); } catch { /* noop */ } });
      localTracksRef.current = [];
    } catch { /* noop */ }
    try { room?.disconnect?.(); } catch { /* noop */ }
  }, [room]);

  const toggleMic = () => {
    const audio = localTracksRef.current.find(t => t.kind === "audio");
    if (!audio) return;
    if (audio.isEnabled) audio.disable(); else audio.enable();
    setMicOn(audio.isEnabled);
  };
  const toggleCam = () => {
    const v = localTracksRef.current.find(t => t.kind === "video");
    if (!v) return;
    if (v.isEnabled) v.disable(); else v.enable();
    setCamOn(v.isEnabled);
  };

  // ─── MediaRecorder for transcription ───────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderStreamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recorderChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) recorderChunksRef.current.push(e.data); };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error(`Recording failed: ${e.message}`);
    }
  };
  const stopRecordingAndTranscribe = async () => {
    const mr = recorderRef.current;
    if (!mr) return;
    setRecording(false);
    setTranscribing(true);
    await new Promise((resolve) => {
      mr.onstop = resolve;
      try { mr.stop(); } catch { resolve(); }
    });
    try { recorderStreamRef.current?.getTracks?.().forEach(t => t.stop()); } catch { /* noop */ }
    const blob = new Blob(recorderChunksRef.current, { type: "audio/webm" });
    blob.name = `${roomName || "call"}.webm`;
    try {
      const res = await transcribeAudio({ blob, customer_id: customerId, save_as_note: true });
      toast.success(`Transcript saved (${res.chars} chars). Look for "Call transcript" in the customer Notes panel.`);
    } catch (e) {
      toast.error(`Transcribe failed: ${extractErr(e, "unknown error").message}`);
    } finally {
      setTranscribing(false);
    }
  };

  const endCall = async () => {
    try { await logVideoEnd({ room: roomName, duration_seconds: elapsed }); } catch { /* noop */ }
    teardown();
    setPhase("ended");
    onClose?.();
  };

  if (!open) return null;
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="video-call-modal">
      <div className="w-full max-w-5xl bg-slate-900 rounded-lg shadow-2xl border border-slate-700 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-3 text-white">
            <VideoCamera size={18} weight="fill" className="text-emerald-400" />
            <div>
              <div className="text-sm font-semibold">Video Call · {customerName || "Customer"}</div>
              <div className="text-[11px] text-slate-400">
                {personName ? `with ${personName} · ` : ""}room: <span className="font-mono">{roomName || "…"}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-slate-300 text-xs">
            {phase === "live" && <span className="font-mono text-emerald-400" data-testid="video-elapsed">{fmt(elapsed)}</span>}
            <button onClick={endCall} data-testid="video-close-btn" className="text-slate-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="relative bg-black flex-1 min-h-[400px]">
          {phase === "connecting" && (
            <div className="absolute inset-0 flex items-center justify-center text-white gap-3">
              <Spinner size={20} className="animate-spin" /> Connecting to Twilio…
            </div>
          )}
          {phase === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-rose-200 gap-2 p-6 text-center">
              <div className="font-semibold">Couldn't start video call</div>
              <div className="text-sm text-rose-300/80">{error}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 h-full">
            <div className="bg-slate-800 rounded-md overflow-hidden flex flex-col">
              <div className="text-[10px] uppercase text-slate-400 px-2 py-1">You · {identity}</div>
              <div ref={localVideoRef} className="flex-1 [&_video]:w-full [&_video]:h-full [&_video]:object-cover" />
            </div>
            <div className="bg-slate-800 rounded-md overflow-hidden flex flex-col">
              <div className="text-[10px] uppercase text-slate-400 px-2 py-1">
                Remote · {participants.length === 0 ? "waiting…" : participants.map(p => p.identity).join(", ")}
              </div>
              <div ref={remoteContainerRef} className="flex-1 grid place-items-center p-2 gap-2 [&_video]:max-w-full [&_video]:rounded" />
            </div>
          </div>
        </div>

        {/* Footer controls */}
        <div className="bg-slate-800 border-t border-slate-700 px-4 py-3 flex items-center justify-between">
          <div className="text-[11px] text-slate-400">
            Share this link with {personName || "the contact"}: <span className="font-mono text-slate-300">{shareableLink || "…"}</span>{" "}
            <button
              onClick={() => { try { navigator.clipboard.writeText(shareableLink); } catch { /* noop */ } }}
              data-testid="video-copy-link-btn"
              className="ml-2 underline hover:text-white"
            >Copy</button>
          </div>
          <div className="flex items-center gap-2">
            <CtrlBtn onClick={toggleMic} active={micOn} testId="video-mic-toggle"
              icon={micOn ? <Microphone size={14} weight="fill" /> : <MicrophoneSlash size={14} weight="fill" />}
              label={micOn ? "Mute" : "Unmute"} tone={micOn ? "neutral" : "warn"} />
            <CtrlBtn onClick={toggleCam} active={camOn} testId="video-cam-toggle"
              icon={camOn ? <VideoCamera size={14} weight="fill" /> : <VideoCameraSlash size={14} weight="fill" />}
              label={camOn ? "Camera" : "Cam off"} tone={camOn ? "neutral" : "warn"} />
            {!recording ? (
              <CtrlBtn onClick={startRecording} testId="video-record-start"
                icon={<Record size={14} weight="fill" />} label="Record" tone="emerald" />
            ) : (
              <CtrlBtn onClick={stopRecordingAndTranscribe} testId="video-record-stop"
                icon={transcribing ? <Spinner size={14} className="animate-spin" /> : <ArrowsOut size={14} weight="fill" />}
                label={transcribing ? "Transcribing…" : "Stop & Transcribe"} tone="amber" />
            )}
            <CtrlBtn onClick={endCall} testId="video-end-call-btn"
              icon={<Phone size={14} weight="fill" className="rotate-[135deg]" />} label="End call" tone="rose" />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CtrlBtn({ onClick, icon, label, tone = "neutral", testId }) {
  const cls = {
    neutral: "bg-slate-700 hover:bg-slate-600 text-white border-slate-600",
    warn: "bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border-amber-500/40",
    emerald: "bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-100 border-emerald-500/40",
    amber: "bg-amber-600/40 hover:bg-amber-600/60 text-amber-100 border-amber-500/40",
    rose: "bg-rose-600 hover:bg-rose-500 text-white border-rose-500",
  }[tone];
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md border text-xs font-semibold ${cls}`}>
      {icon}<span>{label}</span>
    </button>
  );
}
